import * as XLSX from 'xlsx';
import { ProductStock, StockShortage, Merma } from '../types';

// Helper function to format date
const formatDate = (date: string | Date | null | undefined): string => {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// Helper function to determine stock status
const getStockStatus = (stock: ProductStock): string => {
    if (stock.min_quantity === null || stock.min_quantity === undefined) return 'Sin Mínimo';
    if (stock.reported_stock < stock.min_quantity) return 'Bajo Stock';
    if (stock.reported_stock <= stock.min_quantity * 1.05) return 'Óptimo';
    return 'Excedente';
};

// Transform stock data for "Stock General" sheet
const transformStockData = (stockItems: ProductStock[]) => {
    return stockItems.map(item => ({
        'ID Producto': item.id,
        'Nombre': item.name,
        'Almacén': item.storage_unit_name,
        'ID Almacén': item.storage_unit_id,
        'Acrónimo': item.acronym,
        'Stock Sistema': item.system_stock,
        'Stock Real': item.reported_stock,
        'Stock Mínimo': item.min_quantity ?? 'N/A',
        'Última Actualización': formatDate(item.last_stock_update),
        'Estado': getStockStatus(item),
        'Favorito': item.is_favorite ? 'Sí' : 'No',
        'Siguiendo': item.is_following ? 'Sí' : 'No',
    }));
};

// Transform stock shortages data for "Quiebres de Stock" sheet
const transformShortagesData = (shortages: StockShortage[]) => {
    return shortages.map(shortage => ({
        'ID Quiebre': shortage.shortage_id,
        'Almacén': shortage.storage_unit_name,
        'ID Almacén': shortage.storage_unit_id,
        'Item': shortage.item_name || shortage.ingredient_name || 'N/A',
        'ID Item': shortage.item_id || shortage.ingredient_id || 'N/A',
        'Ingrediente': shortage.ingredient_name || 'N/A',
        'ID Ingrediente': shortage.ingredient_id || 'N/A',
        'Cantidad': shortage.quantity,
        'Unidad de Medida': shortage.unidad_medida_name,
        'ID Unidad': shortage.unidad_medida_id,
        'Resuelto': shortage.solved ? 'Sí' : 'No',
        'Fecha Local': formatDate(shortage.local_timestamp),
        'Fecha Servidor': formatDate(shortage.timestamp),
        'ID Local': shortage.local_id,
        'ID Sesión': shortage.local_session_id || 'N/A',
    }));
};

// Transform mermas data for "Mermas" sheet
const transformMermasData = (mermas: Merma[]) => {
    return mermas.map(merma => ({
        'ID Merma': merma.merma_id,
        'Almacén': merma.storage_unit_name,
        'ID Almacén': merma.storage_unit_id,
        'Ingrediente': merma.ingredient_name,
        'ID Ingrediente': merma.ingredient_id,
        'Cantidad': merma.quantity,
        'Unidad de Medida': merma.unidad_medida_acronym,
        'ID Unidad': merma.unidad_medida_id,
        'Usuario': merma.user_name || 'N/A',
        'ID Usuario': merma.user_id || 'N/A',
        'ID Local': merma.local_id,
        'ID Sesión': merma.local_session_id || 'N/A',
        'Fecha Local': formatDate(merma.local_timestamp),
        'Fecha Servidor': formatDate(merma.timestamp),
    }));
};

// Main export function
export const exportStockToExcel = (
    stockItems: ProductStock[],
    shortages: StockShortage[],
    mermas: Merma[],
    fileName?: string
) => {
    try {
        // Transform data for all sheets
        const stockData = transformStockData(stockItems);
        const shortagesData = transformShortagesData(shortages);
        const mermasData = transformMermasData(mermas);
        
        // Create workbook
        const wb = XLSX.utils.book_new();
        
        // Create "Stock General" sheet - always include headers even if empty
        const wsStock = stockData.length > 0 
            ? XLSX.utils.json_to_sheet(stockData)
            : XLSX.utils.json_to_sheet([], {
                header: ['ID Producto', 'Nombre', 'Almacén', 'ID Almacén', 'Acrónimo', 
                         'Stock Sistema', 'Stock Real', 'Stock Mínimo', 'Última Actualización', 
                         'Estado', 'Favorito', 'Siguiendo']
            });
        
        // Set column widths for Stock General sheet
        const stockColWidths = [
            { wch: 12 },  // ID Producto
            { wch: 30 },  // Nombre
            { wch: 20 },  // Almacén
            { wch: 12 },  // ID Almacén
            { wch: 10 },  // Acrónimo
            { wch: 12 },  // Stock Sistema
            { wch: 12 },  // Stock Real
            { wch: 12 },  // Stock Mínimo
            { wch: 20 },  // Última Actualización
            { wch: 15 },  // Estado
            { wch: 10 },  // Favorito
            { wch: 10 },  // Siguiendo
        ];
        wsStock['!cols'] = stockColWidths;
        
        // Add Stock General sheet to workbook
        XLSX.utils.book_append_sheet(wb, wsStock, 'Stock General');
        
        // Create "Quiebres de Stock" sheet - always include headers even if empty
        const wsShortages = shortagesData.length > 0
            ? XLSX.utils.json_to_sheet(shortagesData)
            : XLSX.utils.json_to_sheet([], {
                header: ['ID Quiebre', 'Almacén', 'ID Almacén', 'Item', 'ID Item', 
                         'Ingrediente', 'ID Ingrediente', 'Cantidad', 'Unidad de Medida', 
                         'ID Unidad', 'Resuelto', 'Fecha Local', 'Fecha Servidor', 
                         'ID Local', 'ID Sesión']
            });
        
        // Set column widths for Quiebres sheet
        const shortagesColWidths = [
            { wch: 12 },  // ID Quiebre
            { wch: 20 },  // Almacén
            { wch: 12 },  // ID Almacén
            { wch: 25 },  // Item
            { wch: 12 },  // ID Item
            { wch: 25 },  // Ingrediente
            { wch: 15 },  // ID Ingrediente
            { wch: 10 },  // Cantidad
            { wch: 18 },  // Unidad de Medida
            { wch: 12 },  // ID Unidad
            { wch: 10 },  // Resuelto
            { wch: 20 },  // Fecha Local
            { wch: 20 },  // Fecha Servidor
            { wch: 10 },  // ID Local
            { wch: 12 },  // ID Sesión
        ];
        wsShortages['!cols'] = shortagesColWidths;
        
        // Add Quiebres sheet to workbook
        XLSX.utils.book_append_sheet(wb, wsShortages, 'Quiebres de Stock');
        
        // Create "Mermas" sheet - always include headers even if empty
        const wsMermas = mermasData.length > 0
            ? XLSX.utils.json_to_sheet(mermasData)
            : XLSX.utils.json_to_sheet([], {
                header: ['ID Merma', 'Almacén', 'ID Almacén', 'Ingrediente', 'ID Ingrediente', 
                         'Cantidad', 'Unidad de Medida', 'ID Unidad', 'Usuario', 'ID Usuario', 
                         'ID Local', 'ID Sesión', 'Fecha Local', 'Fecha Servidor']
            });
        
        // Set column widths for Mermas sheet
        const mermasColWidths = [
            { wch: 12 },  // ID Merma
            { wch: 20 },  // Almacén
            { wch: 12 },  // ID Almacén
            { wch: 25 },  // Ingrediente
            { wch: 15 },  // ID Ingrediente
            { wch: 10 },  // Cantidad
            { wch: 12 },  // Unidad de Medida
            { wch: 12 },  // ID Unidad
            { wch: 20 },  // Usuario
            { wch: 12 },  // ID Usuario
            { wch: 10 },  // ID Local
            { wch: 12 },  // ID Sesión
            { wch: 20 },  // Fecha Local
            { wch: 20 },  // Fecha Servidor
        ];
        wsMermas['!cols'] = mermasColWidths;
        
        // Add Mermas sheet to workbook
        XLSX.utils.book_append_sheet(wb, wsMermas, 'Mermas');
        
        // Generate filename
        const timestamp = new Date().toISOString().split('T')[0];
        const finalFileName = fileName || `Stock_${timestamp}.xlsx`;
        
        // Write file
        XLSX.writeFile(wb, finalFileName);
        
        console.log(`Excel exported successfully: ${finalFileName}`);
        return true;
    } catch (error) {
        console.error('Error generating Excel file:', error);
        throw error;
    }
};

// Export filtered stock by date range and other filters
export const exportFilteredStock = (
    stockItems: ProductStock[],
    shortages: StockShortage[],
    mermas: Merma[],
    startDate?: string,
    endDate?: string,
    fileName?: string
) => {
    const timestamp = new Date().toISOString().split('T')[0];
    const dateRange = startDate && endDate 
        ? `${startDate}_a_${endDate}`
        : timestamp;
    
    const finalFileName = fileName || `Stock_${dateRange}.xlsx`;
    
    return exportStockToExcel(stockItems, shortages, mermas, finalFileName);
};
